import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { jcqAPI } from '../services/api';

// --- BRAND PALETTE (Matched to Dashboard.js) ---
const BRAND = {
    teal: '#75B9BE',
    cream: '#FDF6EC',
    charcoal: '#2D4042',
    coral: '#F28482',
    white: '#FFFFFF',
    grey: '#F0F2F5',
    danger: '#E07A5F',
    warning: '#F2CC8F',
    muted: '#5D7375',
    light: '#F8F9FA'
};

const EDUCATIONAL_TERMS_MAP = {
    audioNarrationEnabled: 'Audio narration accommodation',
    audioNarrationSpeed: 'Audio narration speed adjustment',
    customTutorialStructure: 'Custom working-memory scaffolding',
    keyTermsPerMatchingPage: 'Cognitive load adjustment (terms per page)',
    tutorialFont: 'Dyslexia-friendly font accommodation',
    glossaryLanguageMode: 'EAL language scaffolding mode',
    readingAge: 'Standardised reading age',
    senCondition: 'Primary SEN status',
    senConditions: 'Secondary SEN status updates',
    timeDeficitPercent: 'Recorded processing speed deficit',
    extraTimeTestScore: 'Formal Extra Time assessment score'
};

const INVERTED_BOOLEAN_EDUCATIONAL_TERMS_MAP = {
    noCustomScaffoldingRequired: 'Custom scaffolding provision'
};

const formatEducationalTerm = (field) => {
    if (EDUCATIONAL_TERMS_MAP[field]) return EDUCATIONAL_TERMS_MAP[field];
    if (INVERTED_BOOLEAN_EDUCATIONAL_TERMS_MAP[field]) return INVERTED_BOOLEAN_EDUCATIONAL_TERMS_MAP[field];
    return String(field || '')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^./, (char) => char.toUpperCase());
};

const formatValue = (value) => {
    if (Array.isArray(value)) return value.length ? value.join(', ') : 'None';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (value === null || value === undefined || value === '') return 'Not recorded';
    return String(value);
};

const formatSubjectTag = (subject) => String(subject || '').trim();

const valuesMatch = (before, after) => JSON.stringify(before ?? null) === JSON.stringify(after ?? null);

const getYearSortValue = (yearGroup = '') => {
    const match = String(yearGroup).match(/\d+/);
    if (match) return Number(match[0]);
    if (String(yearGroup).includes('+')) return 99;
    return 999;
};

const GLOSSARY_MODE_LABELS = {
    PRIMARY_TEXT_ENGLISH_GLOSSARY: 'Primary text with English glossary',
    ENGLISH_TEXT_PRIMARY_GLOSSARY: 'English text with primary-language glossary'
};

const BACKGROUND_LABELS = {
    '#FDF6EC': 'Cream reading background',
    '#EAF4F4': 'Pale blue reading background',
    '#FFEDE8': 'Soft peach reading background'
};

const CONDITION_SCAFFOLDS = [
    {
        match: (value) => value.includes('dyslexia'),
        title: 'Dyslexia',
        text: ['Short paragraphs', 'Brief direct sentences', 'Clear vocabulary with immediate definitions', 'Key-word decoding focus'],
        visual: ['Brief slide narration', 'Simple high-contrast images', 'Low visual clutter']
    },
    {
        match: (value) => value.includes('working memory') || value.includes('memory'),
        title: 'Working memory difficulties',
        text: ['Small information chunks', 'Natural repetition of key vocabulary', 'Concrete examples', 'Limited new concepts per explainer'],
        visual: ['One concept per slide', 'Repeated key vocabulary', 'Concrete memorable images']
    },
    {
        match: (value) => value.includes('processing') || value.includes('slow'),
        title: 'Processing difficulties',
        text: ['Simple subject-verb-object syntax', 'One idea per sentence', 'Sequential ordering', 'Reduced complex clauses/passive voice'],
        visual: ['Simple sentence narration', 'Uncluttered compositions', 'Logical slide sequence']
    },
    {
        match: (value) => value.includes('language') || value.includes('communication') || value.includes('speech') || value.includes('sld'),
        title: 'Language/communication needs',
        text: ['Simple definitions for subject vocabulary', 'Everyday language before technical terms', 'Relatable examples/analogies', 'Reduced idioms and figurative language'],
        visual: ['Everyday narration', 'Vocabulary shown through direct images', 'Literal rather than abstract visuals']
    },
    {
        match: (value) => value.includes('autism') || value.includes('asc') || value.includes('asd'),
        title: 'Autism Spectrum Condition',
        text: ['Literal explicit language', 'Concrete explanations', 'Consistent terminology', 'Direct expectations/context', 'Logical cause-and-effect structure'],
        visual: ['Literal concrete images', 'Predictable visual structure', 'Neutral sensory load', 'Explicit visual context']
    }
];

const normalizeConditionList = (baseline = {}) => {
    const conditions = Array.isArray(baseline.senConditions) && baseline.senConditions.length > 0
        ? baseline.senConditions
        : [baseline.senCondition];

    return conditions
        .map((condition) => String(condition || '').trim())
        .filter((condition) => condition && condition.toLowerCase() !== 'none');
};

const getConditionScaffolds = (baseline = {}) => {
    const conditions = normalizeConditionList(baseline);
    const matched = [];

    conditions.forEach((condition) => {
        const normalized = condition.toLowerCase();
        const scaffold = CONDITION_SCAFFOLDS.find((item) => item.match(normalized));
        if (!scaffold) return;
        if (!matched.some((item) => item.title === scaffold.title)) matched.push(scaffold);
    });

    return matched;
};

const formatAudioSpeed = (speed) => {
    const numeric = Number(speed);
    if ([0.5, 0.75, 1].includes(numeric)) return `${numeric}x`;
    return '1x';
};

const formatTimelineObservation = (change = {}) => {
    const term = change.label || formatEducationalTerm(change.field);

    if (change.created) {
        return <>Baseline {term} established as: <strong style={{ color: BRAND.teal }}>{formatValue(change.after)}</strong>.</>;
    }

    if (INVERTED_BOOLEAN_EDUCATIONAL_TERMS_MAP[change.field]) {
        if (change.after === false) return <>{term} trialled and enabled.</>;
        if (change.after === true) return <>{term} removed from normal way of working.</>;
    }

    if (change.after === true) return <>{term} trialled and enabled.</>;
    if (change.after === false) return <>{term} removed from normal way of working.</>;

    return <>{term} updated to: <strong style={{ color: BRAND.teal }}>{formatValue(change.after)}</strong>.</>;
};

const buildActiveScaffoldingGroups = (baseline = {}, interactionSummary = {}) => {
    const groups = [];
    const customScaffoldingActive = baseline.noCustomScaffoldingRequired !== true;
    const conditionScaffolds = customScaffoldingActive ? getConditionScaffolds(baseline) : [];

    if (conditionScaffolds.length > 0) {
        groups.push({
            title: 'Modified Language & Presentation',
            tone: 'teal',
            items: conditionScaffolds.flatMap((scaffold) => ([
                {
                    label: `${scaffold.title}: Language Modifications`,
                    detail: scaffold.text.join('; ')
                },
                {
                    label: `${scaffold.title}: Visual Formatting`,
                    detail: scaffold.visual.join('; ')
                }
            ]))
        });
    }

    const assistiveItems = [];
    if (baseline.audioNarrationEnabled) {
        assistiveItems.push({ label: `Audio narration (speed: ${formatAudioSpeed(baseline.audioNarrationSpeed)})`, detail: 'Narration controls are enabled across slides, explainers, glossary matching, and quiz questions.' });
    }
    const fontLabel = baseline.tutorialFont || 'OpenDyslexic';
    assistiveItems.push({ label: `${fontLabel} font`, detail: fontLabel === 'OpenDyslexic' ? 'Tutorial player renders with the dyslexia-friendly OpenDyslexic font.' : 'Tutorial player uses the selected profile font.' });
    if (customScaffoldingActive && baseline.customTutorialStructure) {
        assistiveItems.push({ label: `Custom glossary structure (${baseline.keyTermsPerMatchingPage || 4} key term${Number(baseline.keyTermsPerMatchingPage) === 1 ? '' : 's'} per page)`, detail: 'Glossary matching is split to reduce working-memory and retrieval load.' });
    }
    if (assistiveItems.length > 0) {
        groups.push({ title: 'Assistive technology & tutorial controls', tone: 'coral', items: assistiveItems });
    }

    if (customScaffoldingActive && baseline.eal) {
        groups.push({
            title: 'EAL language scaffolding',
            tone: 'warning',
            items: [{
                label: GLOSSARY_MODE_LABELS[baseline.glossaryLanguageMode] || 'Primary text with English glossary',
                detail: `Primary language: ${baseline.primaryLanguage || 'Not recorded'}. Glossary/translation mode is applied during tutorial generation.`
            }]
        });
    }

    const observedItems = [];
    if (interactionSummary.fontSizeAdjusted) {
        observedItems.push({ label: 'Adjusted font size used', detail: `Observed ${interactionSummary.fontSizeAdjustmentCount || 1} font-size change${Number(interactionSummary.fontSizeAdjustmentCount) === 1 ? '' : 's'} in completed tutorial attempts.` });
    }
    const backgrounds = Array.isArray(interactionSummary.readingBackgrounds) ? interactionSummary.readingBackgrounds : [];
    const nonDefaultBackgrounds = backgrounds.filter((color) => color && color !== BRAND.cream);
    if (nonDefaultBackgrounds.length > 0) {
        observedItems.push({ label: 'Alternative reading background used', detail: [...new Set(nonDefaultBackgrounds)].map((color) => BACKGROUND_LABELS[color] || color).join(', ') });
    }
    if (observedItems.length > 0) {
        groups.push({ title: 'Observed in-player access choices', tone: 'muted', items: observedItems });
    }

    return groups;
};

const YEAR_GROUP_AGES = {
    'nursery': '3/4', 'reception': '4/5',
    'year 1': '5/6', 'year 2': '6/7', 'year 3': '7/8', 'year 4': '8/9',
    'year 5': '9/10', 'year 6': '10/11', 'year 7': '11/12', 'year 8': '12/13',
    'year 9': '13/14', 'year 10': '14/15', 'year 11': '15/16',
    'year 12': '16/17', 'year 13': '17/18'
};

const formatYearGroup = (yearGroup) => {
    if (!yearGroup) return 'Not recorded';
    const age = YEAR_GROUP_AGES[String(yearGroup).toLowerCase().trim()];
    return age ? `${yearGroup} (Age ${age})` : yearGroup;
};

const buildStudentHierarchy = (students = []) => {
    const yearMap = new Map();

    students.forEach((student) => {
        const yearName = student.homeworkScheduleId?.yearGroup || 'Year not set';

        if (!yearMap.has(yearName)) {
            yearMap.set(yearName, { name: yearName, students: [] });
        }

        yearMap.get(yearName).students.push(student);
    });

    return Array.from(yearMap.values())
        .sort((a, b) => getYearSortValue(a.name) - getYearSortValue(b.name) || a.name.localeCompare(b.name))
        .map((year) => ({
            ...year,
            students: year.students.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
        }));
};

const getTimelineChanges = (item = {}) => {
    const before = item.before || {};
    const after = item.after || {};
    const fields = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]));

    if (item.action === 'CREATED' && fields.length) {
        return fields
            .filter((field) => after[field] !== undefined && after[field] !== null && after[field] !== '')
            .map((field) => ({ field, label: formatEducationalTerm(field), before: undefined, after: after[field], created: true }));
    }

    return fields
        .filter((field) => !valuesMatch(before[field], after[field]))
        .map((field) => ({ field, label: formatEducationalTerm(field), before: before[field], after: after[field] }));
};

const StatCard = ({ label, value, helper }) => (
    <div className="dash-card" style={{ padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div>
            <p style={{ color: BRAND.muted, fontSize: '0.85rem', fontWeight: 800, margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {label}
            </p>
            {helper && <p style={{ color: BRAND.muted, margin: 0, fontSize: '0.9rem', fontWeight: 500 }}>{helper}</p>}
        </div>
        <div style={{ textAlign: 'right' }}>
            <p style={{ color: BRAND.charcoal, fontFamily: 'Nunito, sans-serif', fontSize: '1.4rem', fontWeight: 800, margin: 0 }}>
                {value}
            </p>
        </div>
    </div>
);

const getDeficitBadge = (deficit) => {
    const numericDeficit = Number(deficit);
    if (Number.isFinite(numericDeficit) && numericDeficit >= 25) {
        return { background: 'rgba(224, 122, 95, 0.18)', border: BRAND.danger, color: '#8A2C1D', text: 'Exceeds 25% threshold' };
    }
    if (Number.isFinite(numericDeficit) && numericDeficit >= 15) {
        return { background: 'rgba(242, 204, 143, 0.28)', border: BRAND.warning, color: '#7A4E00', text: 'Borderline deficit' };
    }
    return { background: '#DFF3E4', border: '#1F7A3A', color: '#1F7A3A', text: 'Below threshold' };
};

const SubjectMetricCard = ({ metric }) => {
    const subjectName = String(metric?.subjectName || '').trim();
    if (!subjectName) return null;
    const deficit = Number.isFinite(Number(metric?.timeDeficitPercent)) ? Number(metric.timeDeficitPercent) : 0;
    const averageTime = Number.isFinite(Number(metric?.averageTimePerQuestionSeconds)) ? Number(metric.averageTimePerQuestionSeconds) : 0;
    const activeScaffolds = Array.isArray(metric?.activeScaffolds) ? metric.activeScaffolds : [];
    const badge = getDeficitBadge(deficit);

    return (
        <article className="dash-card jcq-subject-card" style={{ padding: '20px', background: BRAND.light, borderColor: '#E0E0E0', boxShadow: 'none' }}>
            {/* Header & Volume Metrics */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '10px', marginBottom: '18px' }}>
                <h3 style={{ color: BRAND.charcoal, fontSize: '1.3rem', margin: 0 }}>{subjectName}</h3>
                {(metric.tutorialsPerWeek > 0) && (
                    <span style={{ background: BRAND.white, border: '1px solid #D8DEE2', borderRadius: '6px', color: BRAND.muted, fontSize: '0.85rem', fontWeight: 700, padding: '4px 8px' }}>
                        {metric.tutorialsPerWeek} tutorials/week
                    </span>
                )}
            </div>

            {/* History of Need */}
            <div style={{ borderBottom: '1px solid #D8DEE2', marginBottom: '18px', paddingBottom: '18px' }}>
                <h4 style={{ color: BRAND.charcoal, fontSize: '1rem', margin: '0 0 12px' }}>History of Need (Speed Deficit)</h4>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '10px' }}>
                    <span style={{ color: BRAND.muted, fontWeight: 700 }}>Processing Deficit:</span>
                    <span style={{ color: BRAND.charcoal, fontWeight: 800 }}>{deficit}% longer</span>
                </div>
                <span style={{ background: badge.background, border: `1px solid ${badge.border}`, borderRadius: '999px', color: badge.color, display: 'inline-flex', fontSize: '0.78rem', fontWeight: 800, padding: '5px 10px', textTransform: 'uppercase' }}>
                    {badge.text}
                </span>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginTop: '12px' }}>
                    <span style={{ color: BRAND.muted, fontWeight: 700 }}>Avg. Time per Question:</span>
                    <span style={{ color: BRAND.charcoal, fontWeight: 800 }}>{averageTime}s</span>
                </div>
            </div>

            {/* History of Provision */}
            <div>
                <h4 style={{ color: BRAND.charcoal, fontSize: '1rem', margin: '0 0 12px' }}>History of Provision (Normal Way of Working)</h4>
                {activeScaffolds.length > 0 ? (
                    <ul style={{ display: 'flex', flexDirection: 'column', gap: '12px', listStyle: 'none', margin: 0, padding: 0 }}>
                        {activeScaffolds.map((scaffold, idx) => {
                            const isObj = typeof scaffold === 'object' && scaffold !== null;
                            const name = isObj ? scaffold.name : scaffold;
                            const startDate = isObj && scaffold.startDate ? new Date(scaffold.startDate).toLocaleDateString('en-GB') : null;

                            return (
                                <li key={idx} style={{ alignItems: 'flex-start', color: BRAND.charcoal, display: 'flex', gap: '8px' }}>
                                    <span aria-hidden="true" style={{ color: '#1F7A3A', fontWeight: 900 }}>✓</span>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                        <span style={{ fontWeight: 800 }}>{name}</span>
                                        {startDate && (
                                            <span style={{ color: BRAND.muted, fontSize: '0.85rem', fontWeight: 600 }}>
                                                Provision started: {startDate}
                                            </span>
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                ) : (
                    <p style={{ color: BRAND.muted, fontStyle: 'italic', fontWeight: 600, margin: 0 }}>No active scaffolds consistently used in this subject.</p>
                )}
            </div>
        </article>
    );
};

// ─── JCQ FORM HELPERS ────────────────────────────────────────────────────────

const F = {
    overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.65)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', overflowY: 'auto', padding: '30px 16px' },
    paper: { background: '#fff', width: '100%', maxWidth: 840, padding: '36px 40px', fontFamily: 'Arial, sans-serif', fontSize: '0.88rem', color: '#000', lineHeight: 1.5 },
    header: { borderBottom: '3px solid #000', paddingBottom: 10, marginBottom: 18 },
    sectionBar: { background: '#000', color: '#fff', padding: '5px 10px', fontWeight: 700, fontSize: '0.85rem', marginTop: 20, marginBottom: 10 },
    fieldLabel: { fontWeight: 700, fontSize: '0.78rem', marginTop: 10, marginBottom: 3 },
    fieldBox: { border: '1px solid #555', padding: '6px 8px', minHeight: 52, background: '#fafafa', fontSize: '0.85rem', whiteSpace: 'pre-wrap' },
    charNote: { fontSize: '0.68rem', color: '#888', textAlign: 'right', marginTop: 1 },
    tableCell: { padding: '5px 7px', borderRight: '1px solid #555', fontSize: '0.78rem' },
    tableCellLast: { padding: '5px 7px', fontSize: '0.78rem' },
    note: { fontSize: '0.78rem', color: '#555', background: '#fffbe6', border: '1px solid #d4c040', borderRadius: 3, padding: '6px 10px', marginBottom: 10 },
};

const FCheckbox = ({ checked }) => (
    <span style={{ display: 'inline-block', width: 13, height: 13, border: '1.5px solid #333', background: checked ? '#333' : '#fff', marginRight: 7, verticalAlign: 'middle', textAlign: 'center', lineHeight: '11px', fontSize: 9, color: '#fff', flexShrink: 0 }}>
        {checked ? '✓' : ''}
    </span>
);

const FField = ({ label, value, maxChars, minHeight = 52 }) => (
    <div style={{ marginBottom: 10 }}>
        {label && <div style={F.fieldLabel}>{label}</div>}
        <div style={{ ...F.fieldBox, minHeight }}>{value || ''}</div>
        {maxChars && <div style={F.charNote}>Max {maxChars.toLocaleString()} characters</div>}
    </div>
);

const FInfoRow = ({ cells }) => (
    <div style={{ display: 'grid', gridTemplateColumns: cells.map(() => '1fr').join(' '), border: '1px solid #555', marginBottom: 8 }}>
        {cells.map((c, i) => (
            <div key={i} style={{ padding: '5px 8px', borderRight: i < cells.length - 1 ? '1px solid #555' : 'none' }}>
                <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#555', textTransform: 'uppercase' }}>{c.label}</div>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, marginTop: 1 }}>{c.value || ''}</div>
            </div>
        ))}
    </div>
);

const FScoreTable = ({ title }) => (
    <div style={{ marginBottom: 14 }}>
        <div style={{ fontWeight: 700, fontSize: '0.82rem', marginBottom: 4 }}>{title}</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #555' }}>
            <thead>
                <tr style={{ background: '#f0f0f0' }}>
                    {['Name of test (and subtest)', 'Test ceiling', 'Date of administration', 'Standardised score'].map((h, i, a) => (
                        <th key={h} style={{ ...F.tableCell, fontWeight: 700, borderRight: i < a.length - 1 ? '1px solid #555' : 'none', borderBottom: '1px solid #555', textAlign: 'left' }}>{h}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                <tr>
                    {[0,1,2,3].map(i => <td key={i} style={{ ...(i < 3 ? F.tableCell : F.tableCellLast), height: 28, borderTop: '1px solid #eee' }}></td>)}
                </tr>
            </tbody>
        </table>
    </div>
);

export const Form8Modal = ({ baseline, difficultiesText, nwowText, onClose }) => {
    const sen = Array.isArray(baseline.senConditions) && baseline.senConditions.length
        ? baseline.senConditions.join(', ')
        : baseline.senCondition || '';
    const isEAL = baseline.eal && baseline.eal !== 'No';
    const hasAudio = Boolean(baseline.audioNarrationEnabled);
    const currentYear = new Date().getFullYear();
    const academicYear = `${currentYear}–${currentYear + 1}`;

    const otherInfo = [
        isEAL ? `EAL: Candidate's primary language is ${baseline.primaryLanguage || 'not English'}. Difficulties are present in the primary language and are not attributable to limited English acquisition.` : '',
        sen && sen !== 'None' ? `Co-occurring condition(s): ${sen}.` : '',
    ].filter(Boolean).join('\n\n');

    return (
        <div style={F.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
            <div style={F.paper}>
                <div className="jcq-no-print" style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginBottom: 20 }}>
                    <button className="btn-base btn-charcoal" onClick={() => window.print()}>🖨 Print / Save as PDF</button>
                    <button className="btn-base btn-outline" onClick={onClose}>✕ Close</button>
                </div>

                <div style={F.header}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                            <div style={{ fontSize: '0.68rem', color: '#555', fontWeight: 700 }}>JCQ/AA/LD Form 8 © JCQ 2025 — Effective from 1 September 2025</div>
                            <div style={{ fontWeight: 900, fontSize: '1.3rem', marginTop: 2 }}>Profile of learning difficulties</div>
                        </div>
                        <div style={{ fontSize: '0.68rem', color: '#555', textAlign: 'right' }}>Keep on file for<br />inspection purposes</div>
                    </div>
                    <div style={{ fontSize: '0.78rem', marginTop: 8, color: '#444' }}>
                        For candidates with learning difficulties requiring 25% extra time and/or a scribe (not subject to a current EHCP or Statement of Special Educational Needs).
                    </div>
                </div>

                <FInfoRow cells={[{ label: 'Candidate Name', value: baseline.name }, { label: 'Candidate Number', value: '' }, { label: 'Date of Birth', value: '' }]} />
                <FInfoRow cells={[{ label: 'Academic Year(s) Covered', value: academicYear }, { label: 'First Examination Series', value: 'Summer 2026' }, { label: 'Centre Name', value: '' }]} />
                <FInfoRow cells={[{ label: 'Centre Number', value: '' }, { label: 'Centre Email Address', value: '' }, { label: 'SEN Condition(s)', value: sen }]} />

                <div style={F.sectionBar}>Part 1 — Centre-based evidence</div>
                <div style={{ fontSize: '0.78rem', color: '#444', marginBottom: 10 }}>
                    This section must be fully completed by the SENCo, Access Arrangements Coordinator, or the assessor working within the centre, and given to the assessor before the candidate is assessed.
                </div>

                <FField label="Current difficulties in the classroom, internal tests and/or mock examinations — detail how they have impacted on teaching and learning and performance in internal tests/mock examinations. Summarise evidence of feedback from teachers and/or support staff." value={difficultiesText} maxChars={1500} minHeight={80} />
                <FField label="Normal way of working — detail the support and adjustments that are in place for the candidate in the classroom, internal tests/mock examinations." value={nwowText} maxChars={1500} minHeight={80} />

                {hasAudio && (
                    <div style={{ marginBottom: 14, borderLeft: '3px solid #333', paddingLeft: 10 }}>
                        <div style={{ fontWeight: 700, fontSize: '0.82rem', marginBottom: 6 }}>Evidence for a computer reader/reader</div>
                        <div style={{ marginBottom: 6, fontSize: '0.85rem' }}><FCheckbox checked={true} />Does the candidate require a computer reader/reader in addition to extra time? <strong>Yes</strong></div>
                        <FField label="What is the nature of the candidate's difficulty?" value={`${baseline.name || 'The candidate'} requires audio narration support due to ${sen && sen !== 'None' ? sen : 'their processing difficulty'}. Audio narration has been consistently enabled across all tutorial sessions.`} maxChars={1500} />
                        <FField label="Confirm and describe how the use of a computer reader/reader is the candidate's normal way of working in the centre." value={`Audio narration is enabled as standard for all internal tutorial sessions. The candidate accesses narration consistently across slides, explainers, glossary exercises, and quiz questions as part of their normal classroom and assessment routine.`} maxChars={1500} />
                    </div>
                )}

                <FField label="If the candidate has previously been granted access arrangements by an awarding body, please specify." maxChars={650} minHeight={36} />
                <FField label="Other relevant information (e.g. EAL issues; co-occurring conditions)." value={otherInfo} maxChars={3000} minHeight={48} />

                <div style={F.sectionBar}>Part 2 — Assessment evidence (to be completed by the qualified assessor)</div>
                <div style={F.note}>⚠ This section must be completed by an appropriately qualified assessor (HCPC-registered psychologist or specialist teacher assessor with a current SpLD Assessment Practising Certificate). Record standardised test results below.</div>

                <FScoreTable title="Evidence for extra time — Reading speed (continuous text)" />
                <FScoreTable title="Evidence for extra time — Writing speed (continuous text)" />
                <FScoreTable title="Evidence for extra time — Cognitive processing / fluency" />

                <FInfoRow cells={[{ label: 'Name of Assessor', value: '' }, { label: 'Qualification / Certificate No.', value: '' }, { label: 'Date', value: '' }]} />
                <FField label="Assessor signature" minHeight={36} />

                <div style={F.sectionBar}>Part 3 — Access arrangements to be applied for</div>
                <div style={{ marginBottom: 16 }}>
                    {[
                        { checked: true,     label: '25% extra time' },
                        { checked: hasAudio, label: 'Computer reader/reader (alongside 25% extra time)' },
                        { checked: false,    label: 'Scribe / speech recognition technology / word processor with grammar/spell check and/or predictive text switched on' },
                        { checked: false,    label: '26% to 50% extra time' },
                        { checked: false,    label: 'Language modifier' },
                        { checked: false,    label: 'Supervised rest breaks (alongside 25% extra time and/or a scribe)' },
                    ].map((item, i) => (
                        <div key={i} style={{ marginBottom: 6, fontSize: '0.85rem' }}><FCheckbox checked={item.checked} />{item.label}</div>
                    ))}
                </div>

                <div style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: 6 }}>Declaration</div>
                <p style={{ fontSize: '0.82rem', marginBottom: 10 }}>I am satisfied that the information provided on this form is accurate. I fully support the application and confirm that the candidate is/will be appropriately entered for the examination(s) concerned and will be able to demonstrate the assessment objectives required by the specification(s).</p>
                <FInfoRow cells={[{ label: 'Name', value: '' }, { label: 'Role', value: 'SENCo / Access Arrangements Coordinator' }, { label: 'Date', value: '' }]} />
                <FField label="Signature" minHeight={36} />
                <div style={{ fontSize: '0.72rem', color: '#555', marginTop: 16, borderTop: '1px solid #ccc', paddingTop: 10 }}>
                    A signed and fully completed Form 8 must be retained on file by the SENCo/Access Arrangements Coordinator for inspection purposes to support an approved application processed online.
                </div>
            </div>
        </div>
    );
};

const Form9Modal = ({ baseline, difficultiesText, nwowText, onClose }) => {
    const sen = Array.isArray(baseline.senConditions) && baseline.senConditions.length
        ? baseline.senConditions.join(', ')
        : baseline.senCondition || '';
    const isEAL = baseline.eal && baseline.eal !== 'No';
    const hasAudio = Boolean(baseline.audioNarrationEnabled);
    const currentYear = new Date().getFullYear();
    const academicYear = `${currentYear}–${currentYear + 1}`;

    return (
        <div style={F.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
            <div style={F.paper}>
                <div className="jcq-no-print" style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginBottom: 20 }}>
                    <button className="btn-base btn-charcoal" onClick={() => window.print()}>🖨 Print / Save as PDF</button>
                    <button className="btn-base btn-outline" onClick={onClose}>✕ Close</button>
                </div>

                <div style={F.header}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                            <div style={{ fontSize: '0.68rem', color: '#555', fontWeight: 700 }}>JCQ/AA Form 9 © JCQ 2025 — Effective from 1 September 2025</div>
                            <div style={{ fontWeight: 900, fontSize: '1.3rem', marginTop: 2 }}>Profile of need</div>
                        </div>
                        <div style={{ fontSize: '0.68rem', color: '#555', textAlign: 'right' }}>Keep on file for<br />inspection purposes</div>
                    </div>
                    <div style={{ fontSize: '0.78rem', marginTop: 8, color: '#444' }}>
                        For candidates with communication and interaction needs, medical conditions, sensory/physical needs, social/emotional/mental health needs, or learning difficulties subject to a current EHCP, Statement of SEN, or IDP.
                    </div>
                </div>

                <FInfoRow cells={[{ label: 'Candidate Name', value: baseline.name }, { label: 'Candidate Number', value: '' }, { label: 'Date of Birth', value: '' }]} />
                <FInfoRow cells={[{ label: 'Academic Year(s) Covered', value: academicYear }, { label: 'First Examination Series', value: 'Summer 2026' }, { label: 'Centre Name', value: '' }]} />
                <FInfoRow cells={[{ label: 'Centre Number', value: '' }, { label: 'Centre Email Address', value: '' }, { label: 'SEN Condition(s)', value: sen }]} />

                <FField label="If the candidate has previously been granted access arrangements by an awarding body, please specify." maxChars={1500} minHeight={36} />

                <div style={F.sectionBar}>Part 1 — Evidence for 25% extra time and/or a scribe, word processor with grammar/spellcheck and/or predictive text switched on, or speech recognition technology</div>
                <div style={{ fontSize: '0.78rem', color: '#444', marginBottom: 10 }}>
                    See section 5.2 of the JCQ document Access Arrangements and Reasonable Adjustments. Within this section you must paint a picture of the candidate's needs to demonstrate the requirement for the access arrangement(s).
                </div>

                <FField label="Detail the candidate's current difficulties to show how they impact on teaching and learning and performance in internal tests and/or mock examinations. Summarise evidence of feedback from teachers and/or support staff." value={difficultiesText} maxChars={1500} minHeight={80} />
                <FField label="Confirm and describe how 25% extra time (and/or a scribe, word processor with grammar/spellcheck, or speech recognition technology) is the candidate's normal way of working in the centre (i.e. in the classroom, internal tests and/or mock examinations)." value={nwowText} maxChars={1500} minHeight={80} />

                {hasAudio && (
                    <div style={{ marginBottom: 14, borderLeft: '3px solid #333', paddingLeft: 10 }}>
                        <div style={{ fontWeight: 700, fontSize: '0.82rem', marginBottom: 6 }}>Evidence for a computer reader/reader (Part 1)</div>
                        <FField label="What is the nature of the candidate's difficulty?" value={`${baseline.name || 'The candidate'} requires audio narration support due to ${sen && sen !== 'None' ? sen : 'their processing difficulty'}. Audio narration has been consistently enabled across all tutorial sessions.`} maxChars={1500} />
                        <FField label="Confirm and describe how the use of a computer reader/reader is the candidate's normal way of working." value={`Audio narration is enabled as standard for all internal tutorial sessions. The candidate accesses narration consistently across slides, explainers, glossary exercises, and quiz questions as part of their normal classroom and assessment routine.`} maxChars={1500} />
                    </div>
                )}

                {isEAL && (
                    <div style={{ marginBottom: 10, fontSize: '0.82rem', padding: '6px 10px', border: '1px solid #ccc', borderRadius: 3 }}>
                        <strong>EAL note:</strong> Candidate's primary language is {baseline.primaryLanguage || 'not English'}. Difficulties are present in the primary language and are not attributable to limited English acquisition.
                    </div>
                )}

                <div style={F.note}>
                    Samples of evidence required for inspection: (a) internal test/mock exam papers across relevant subjects showing application of 25% extra time; (b) comments and observations from teaching staff in relevant subjects.
                </div>

                <div style={F.sectionBar}>Part 2 — Specialist evidence held on file confirming the candidate's disability</div>
                <div style={{ fontSize: '0.78rem', color: '#444', marginBottom: 8 }}>Indicate the specialist evidence held on file (tick all that apply):</div>
                <div style={{ marginBottom: 16 }}>
                    {[
                        'A current EHCP, Statement of Special Educational Needs or Individual Development Plan',
                        'A letter or report from a HCPC registered psychologist',
                        'A letter or report from a registered specialist medical consultant',
                        'A letter or report from CAMHS',
                        'A letter or report from a psychiatrist',
                        'A letter or report from a speech and language therapist',
                        'A letter or report from the Local Authority Specialist Service',
                        'Other appropriate medical evidence',
                    ].map((item, i) => (
                        <div key={i} style={{ marginBottom: 5, fontSize: '0.85rem' }}><FCheckbox checked={false} />{item}</div>
                    ))}
                </div>

                <div style={F.sectionBar}>Part 3 — Access arrangements required</div>
                <div style={{ marginBottom: 16 }}>
                    {[
                        { checked: true,     label: '25% extra time' },
                        { checked: hasAudio, label: 'Computer reader/reader' },
                        { checked: false,    label: '26% to 50% extra time' },
                        { checked: false,    label: 'Extra time over 50%' },
                        { checked: false,    label: 'Scribe / speech recognition technology / word processor with grammar/spell check and/or predictive text switched on' },
                        { checked: false,    label: 'Supervised rest breaks (Note: this arrangement does not require an online application)' },
                        { checked: false,    label: 'Access to a mobile phone for medical purposes' },
                        { checked: false,    label: 'Remote invigilation through the use of technology' },
                        { checked: false,    label: 'Timetable variation requiring overnight supervision for a candidate with a disability' },
                        { checked: false,    label: 'Timetable variation on the day of the exam for a candidate with a disability' },
                        { checked: false,    label: 'Listening to music/white noise due to a substantial impairment' },
                    ].map((item, i) => (
                        <div key={i} style={{ marginBottom: 6, fontSize: '0.85rem' }}><FCheckbox checked={item.checked} />{item.label}</div>
                    ))}
                </div>

                <div style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: 6 }}>Declaration</div>
                <p style={{ fontSize: '0.82rem', marginBottom: 10 }}>I am satisfied that the information provided on this form is accurate. I fully support the application and confirm that the candidate is/will be appropriately entered for the examination(s) concerned and will be able to demonstrate the assessment objectives required by the specification(s).</p>
                <FInfoRow cells={[{ label: 'Name', value: '' }, { label: 'Role', value: 'SENCo / Access Arrangements Coordinator' }, { label: 'Date', value: '' }]} />
                <FField label="Signature" minHeight={36} />
                <div style={{ fontSize: '0.72rem', color: '#555', marginTop: 16, borderTop: '1px solid #ccc', paddingTop: 10 }}>
                    A signed and fully completed Form 9 must be retained on file by the SENCo/Access Arrangements Coordinator for inspection purposes to support an approved application processed online.
                </div>
            </div>
        </div>
    );
};

const JCQNormalWayOfWorking = () => {
    const [students, setStudents] = useState([]);
    const [selectedStudentId, setSelectedStudentId] = useState('');
    const [profile, setProfile] = useState(null);
    const [loadingStudents, setLoadingStudents] = useState(true);
    const [loadingProfile, setLoadingProfile] = useState(false);
    const [error, setError] = useState('');
    const [copyDifficultiesStatus, setCopyDifficultiesStatus] = useState('');
    const [copyNWoWStatus, setCopyNWoWStatus] = useState('');
    const [showForm8, setShowForm8] = useState(false);
    const [showForm9, setShowForm9] = useState(false);

    useEffect(() => {
        let isMounted = true;

        const loadStudents = async () => {
            setLoadingStudents(true);
            setError('');
            try {
                const response = await jcqAPI.getStudents();
                const studentList = Array.isArray(response?.data) ? response.data : [];
                if (!isMounted) return;
                setStudents(studentList);
            } catch (err) {
                if (isMounted) setError('Unable to load students. Please try again.');
            } finally {
                if (isMounted) setLoadingStudents(false);
            }
        };

        loadStudents();
        return () => { isMounted = false; };
    }, []);

    useEffect(() => {
        if (!selectedStudentId) {
            setProfile(null);
            return;
        }

        let isMounted = true;
        const loadProfile = async () => {
            setLoadingProfile(true);
            setError('');
            try {
                const response = await jcqAPI.getNNowProfile(selectedStudentId);
                if (isMounted) setProfile(response?.data || null);
            } catch (err) {
                if (isMounted) setError('Unable to load JCQ evidence for this student. Please try again.');
            } finally {
                if (isMounted) setLoadingProfile(false);
            }
        };

        loadProfile();
        return () => { isMounted = false; };
    }, [selectedStudentId]);

    const studentHierarchy = useMemo(() => buildStudentHierarchy(students), [students]);

    const baseline = profile?.studentBaseline || {};
    const metrics = profile?.performanceMetrics || {};
    const interactionSummary = profile?.interactionSummary || {};
    const subjectMetrics = (Array.isArray(metrics.subjectMetrics)
        ? metrics.subjectMetrics
        : (Array.isArray(profile?.subjectMetrics) ? profile.subjectMetrics : []))
        .filter((metric) => String(metric?.subjectName || '').trim());
    const timeline = Array.isArray(profile?.scaffoldingTimeline) ? profile.scaffoldingTimeline : [];
    const timeDeficitText = metrics.timeDeficitPercent === null || metrics.timeDeficitPercent === undefined
        ? 'a currently unquantified amount of time'
        : `${metrics.timeDeficitPercent}% longer`;
    const subjectDeficitComparison = subjectMetrics
        .filter((m) => Number.isFinite(Number(m.timeDeficitPercent)) && Number(m.timeDeficitPercent) > 0)
        .map((m) => `${m.subjectName} (+${Math.round(Number(m.timeDeficitPercent))}%)`)
        .join(', ');
    const extraTimeAssessment = profile?.extraTimeAssessment || null;
    const formalAssessmentSentence = extraTimeAssessment
        ? ` This need has been confirmed through a formal assessment of need: in a timed ${extraTimeAssessment.subjects?.length || 3}-subject JCQ test${extraTimeAssessment.subjects?.length ? ` (${extraTimeAssessment.subjects.join(', ')})` : ''}, ${baseline.name || 'the candidate'} required the 25% extra-time allocation to reach the final questions, answering ${extraTimeAssessment.extraTimeCorrect} of ${extraTimeAssessment.extraTimeAnswered} extra-time questions correctly and using the allocation to return and correct ${extraTimeAssessment.correctionsInExtraTime} earlier answers${extraTimeAssessment.extendedWriting?.usedExtraTime ? `; their extended writing response was likewise only completed and redrafted (${extraTimeAssessment.extendedWriting.amendmentsInExtraTime} amendments) during the extra-time phase` : ''} — a direct overlap between the additional time and the marks achieved.`
        : '';
    const provisionStartDate = timeline.length > 0 && timeline[0]?.changedAt ? new Date(timeline[0].changedAt) : null;
    const provisionMonths = provisionStartDate && !Number.isNaN(provisionStartDate.getTime())
        ? Math.max(1, Math.round((Date.now() - provisionStartDate.getTime()) / (30.44 * 24 * 60 * 60 * 1000)))
        : null;
    const provisionDurationText = provisionMonths ? `the past ${provisionMonths} months` : 'an extended period';
    const currentDifficultiesText = `${baseline.name || 'The candidate'} works at a significantly slower rate than their peers${baseline.senCondition && baseline.senCondition !== 'None' ? `, associated with their ${baseline.senCondition}` : ''}. Platform tracking across ${metrics.totalTutorialsCompleted ?? 0} internal assessments shows the candidate taking an average of ${timeDeficitText} than the peer cohort to process and complete timed tasks${subjectDeficitComparison ? `, with the size of the deficit varying by subject: ${subjectDeficitComparison}` : ''}. In internal tests and mock examinations sat under standard timing, the candidate runs out of time, leaving later questions rushed or unattempted, so marks under-represent the understanding demonstrated in untimed classwork. Subject teachers and support staff report the same pattern across subjects: accurate work produced at a markedly slower pace than the rest of the class.${formalAssessmentSentence}`;
    const normalWayOfWorkingText = `25% extra time has been ${baseline.name || 'the candidate'}'s established normal way of working in the centre for ${provisionDurationText}. It is applied as standard across regular homework tasks, in-class tests and pre-teaching activities${baseline.audioNarrationEnabled ? ', alongside text-to-speech audio narration, which the candidate uses routinely to access written material in each of these settings' : ''}, as well as in internal tests and mock examinations. The attached audit trail records this history of need and provision across subjects. With the additional time in place the candidate completes tasks in full and achieves results in line with their classwork; without it, performance deteriorates in line with the difficulties described above.`;

    const handleCopyDifficulties = async () => {
        setCopyDifficultiesStatus('');
        try {
            await navigator.clipboard.writeText(currentDifficultiesText);
            setCopyDifficultiesStatus('Copied to clipboard.');
            setTimeout(() => setCopyDifficultiesStatus(''), 3000);
        } catch (err) {
            setCopyDifficultiesStatus('Copy unavailable. Please select and copy manually.');
        }
    };

    const handleCopyNWoW = async () => {
        setCopyNWoWStatus('');
        try {
            await navigator.clipboard.writeText(normalWayOfWorkingText);
            setCopyNWoWStatus('Copied to clipboard.');
            setTimeout(() => setCopyNWoWStatus(''), 3000);
        } catch (err) {
            setCopyNWoWStatus('Copy unavailable. Please select and copy manually.');
        }
    };

    return (
        <div style={{
            fontFamily: "'Inter', sans-serif",
            backgroundColor: BRAND.cream,
            minHeight: '100vh',
            color: BRAND.charcoal,
            position: 'relative',
            overflow: 'hidden',
            paddingBottom: '60px'
        }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Nunito:wght@700;800&display=swap');
                h1, h2, h3, h4 { font-family: 'Nunito', sans-serif; }

                /* BACKGROUND PATTERN */
                .pattern-bg {
                    background-image: radial-gradient(${BRAND.teal} 15%, transparent 16%), radial-gradient(${BRAND.coral} 15%, transparent 16%);
                    background-size: 60px 60px;
                    background-position: 0 0, 30px 30px;
                    opacity: 0.1;
                    position: absolute;
                    top: 0; left: 0; right: 0; bottom: 0;
                    z-index: 0;
                    pointer-events: none;
                    mask-image: linear-gradient(to bottom, black 0%, transparent 100%);
                    -webkit-mask-image: linear-gradient(to bottom, black 0%, transparent 100%);
                }

                /* CARD STYLES */
                .dash-card {
                    background: ${BRAND.white};
                    border-radius: 16px;
                    border: 2px solid ${BRAND.charcoal};
                    box-shadow: 4px 4px 0px rgba(45, 64, 66, 0.1);
                    transition: transform 0.2s ease, box-shadow 0.2s ease;
                    overflow: hidden;
                }
                .dash-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 6px 6px 0px rgba(45, 64, 66, 0.15);
                }

                /* SOFT POP BUTTONS */
                .btn-base {
                    border: none;
                    border-radius: 10px;
                    padding: 10px 20px;
                    font-family: 'Nunito', sans-serif;
                    font-weight: 800;
                    cursor: pointer;
                    transition: all 0.1s ease;
                    font-size: 0.95rem;
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    border-bottom: 4px solid rgba(0,0,0,0.1); 
                    text-decoration: none;
                }
                .btn-base:active {
                    transform: translateY(2px);
                    border-bottom: 2px solid rgba(0,0,0,0.1);
                    margin-bottom: 2px;
                }

                .btn-coral { background-color: ${BRAND.coral}; color: white; border: 2px solid ${BRAND.charcoal}; border-bottom: 4px solid ${BRAND.charcoal}; }
                .btn-coral:active { border-bottom: 2px solid ${BRAND.charcoal}; }
                
                .btn-charcoal { background-color: ${BRAND.charcoal}; color: white; border: 2px solid ${BRAND.charcoal}; border-bottom: 4px solid rgba(0,0,0,0.4); }
                .btn-charcoal:active { border-bottom: 2px solid rgba(0,0,0,0.4); }

                .btn-outline { background-color: white; color: ${BRAND.charcoal}; border: 2px solid ${BRAND.charcoal}; border-bottom: 4px solid ${BRAND.charcoal}; }
                .btn-outline:active { border-bottom: 2px solid ${BRAND.charcoal}; }
                .btn-outline:hover { background-color: #F9F9F9; }

                /* STUDENT PILL BUTTONS */
                .student-pill {
                    background: ${BRAND.white};
                    border: 2px solid #D8DEE2;
                    border-bottom: 3px solid #D8DEE2;
                    border-radius: 999px;
                    color: ${BRAND.charcoal};
                    cursor: pointer;
                    font-family: 'Nunito', sans-serif;
                    font-weight: 800;
                    padding: 8px 16px;
                    transition: all 0.1s ease;
                }
                .student-pill:hover { border-color: ${BRAND.teal}; color: ${BRAND.teal}; }
                .student-pill.active {
                    background: ${BRAND.teal};
                    border-color: ${BRAND.charcoal};
                    border-bottom: 3px solid ${BRAND.charcoal};
                    color: white;
                }
                .student-pill:active { transform: translateY(1px); border-bottom-width: 2px; }

                /* PRINT STYLES */
                @media print {
                    body { background: #fff !important; }
                    .pattern-bg, .jcq-no-print { display: none !important; }
                    .dash-card, .jcq-print-card { 
                        break-inside: avoid; 
                        box-shadow: none !important; 
                        border: 2px solid #000 !important;
                    }
                    .jcq-subject-matrix { grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)) !important; gap: 16px !important; }
                    .jcq-subject-card { break-inside: avoid; page-break-inside: avoid; }
                    * { color: #000 !important; }
                }
            `}</style>

            <div className="pattern-bg jcq-no-print"></div>

            <main style={{ position: 'relative', zIndex: 1, padding: '40px 20px', maxWidth: '1100px', margin: '0 auto' }}>
                
                {/* HEADER */}
                <div className="jcq-no-print" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '30px', gap: '20px', flexWrap: 'wrap' }}>
                    <div>
                        <Link to="/jcq-hub" style={{ color: BRAND.charcoal, fontWeight: 800, textDecoration: 'none', display: 'inline-block', marginBottom: '16px', fontSize: '0.95rem' }}>← Back to JCQ hub</Link>
                        <p style={{ color: BRAND.coral, fontFamily: 'Nunito, sans-serif', fontWeight: 800, letterSpacing: '0.08em', margin: '0 0 4px', textTransform: 'uppercase', fontSize: '0.9rem' }}>JCQ Evidence Dashboard</p>
                        <h1 style={{ margin: '0 0 10px 0', color: BRAND.charcoal, fontWeight: '800', fontSize: '2.4em', letterSpacing: '-0.5px' }}>
                            Normal Way of Working (NWoW)
                        </h1>
                        <p style={{ margin: 0, color: BRAND.muted, fontSize: '1.1rem', fontWeight: '500', maxWidth: '600px' }}>
                            Review longitudinal scaffolding changes and tutorial performance indicators to justify access arrangements.
                        </p>
                    </div>
                    
                    <button className="btn-base btn-coral" onClick={() => window.print()} style={{ marginTop: '36px' }}>
                        Export Evidence PDF
                    </button>
                </div>

                {/* SELECT STUDENT */}
                <section className="dash-card jcq-no-print" style={{ padding: '24px', marginBottom: '30px' }}>
                    <h2 style={{ fontSize: '1.4rem', margin: '0 0 16px', fontWeight: '800' }}>Choose a student</h2>
                    
                    {loadingStudents && <p style={{ color: BRAND.muted, margin: 0, fontWeight: '700' }}>Loading student roster...</p>}
                    {!loadingStudents && students.length === 0 && <p style={{ color: BRAND.muted, margin: 0 }}>No students found in your roster.</p>}
                    
                    {!loadingStudents && studentHierarchy.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {studentHierarchy.map((year) => (
                                <details key={year.name} style={{ background: BRAND.light, border: `2px solid #E0E0E0`, borderRadius: '12px', padding: '12px 16px' }}>
                                    <summary style={{ cursor: 'pointer', fontFamily: 'Nunito, sans-serif', fontSize: '1.1rem', fontWeight: 800, color: BRAND.charcoal }}>
                                        {year.name} <span style={{ color: BRAND.muted, fontSize: '0.9rem', fontWeight: '600', marginLeft: '8px' }}>({year.students.length} students)</span>
                                    </summary>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '16px' }}>
                                        {year.students.map((student) => (
                                            <button
                                                key={student._id}
                                                type="button"
                                                onClick={() => setSelectedStudentId(student._id)}
                                                className={`student-pill ${selectedStudentId === student._id ? 'active' : ''}`}
                                            >
                                                {student.name}
                                            </button>
                                        ))}
                                    </div>
                                </details>
                            ))}
                        </div>
                    )}
                </section>

                {/* ERROR & LOADING STATES */}
                {error && (
                    <div role="alert" style={{ background: '#FFF1F0', border: `2px solid ${BRAND.danger}`, borderRadius: '12px', color: '#8A2C1D', marginBottom: '24px', padding: '16px', fontWeight: '700' }}>
                        ⚠️ {error}
                    </div>
                )}
                {loadingProfile && <div style={{ color: BRAND.charcoal, fontWeight: 800, fontSize: '1.2rem', padding: '20px 0', textAlign: 'center' }}>Gathering student evidence...</div>}

                {/* PROFILE CONTENT */}
                {profile && !loadingProfile && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
                        
                        {/* PART 1 */}
                        <section className="dash-card jcq-print-card" style={{ padding: '24px', background: 'rgba(255, 255, 255, 0.8)', borderColor: BRAND.teal, borderWidth: '3px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
                                <div>
                                    <h2 style={{ margin: '0 0 6px', fontSize: '1.5rem' }}>Part 1: Form 8 / Form 9 Auto-Generation</h2>
                                    <p style={{ margin: 0, color: BRAND.muted, fontWeight: 600 }}>
                                        Copy statements into the JCQ AAO portal.
                                    </p>
                                </div>
                                {profile && (
                                    <div className="jcq-no-print" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                        <button className="btn-base btn-teal" onClick={() => setShowForm8(true)}>
                                            📋 View Form 8
                                        </button>
                                        <button className="btn-base btn-coral" onClick={() => setShowForm9(true)}>
                                            📋 View Form 9
                                        </button>
                                    </div>
                                )}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                <div style={{ background: BRAND.white, border: `2px solid ${BRAND.teal}`, borderRadius: '12px', padding: '16px' }}>
                                    <p style={{ color: BRAND.teal, fontFamily: 'Nunito, sans-serif', fontWeight: 800, margin: '0 0 10px', fontSize: '0.9rem', lineHeight: 1.5 }}>Detail the candidate&apos;s current difficulties to show how they impact on teaching and learning and performance in internal tests and/or mock examinations. Summarise evidence of feedback from teachers and/or support staff.</p>
                                    <p style={{ color: BRAND.charcoal, fontSize: '1rem', lineHeight: 1.6, margin: '0 0 14px', fontWeight: 500 }}>{currentDifficultiesText}</p>
                                    <div className="jcq-no-print" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                        <button className="btn-base btn-charcoal" onClick={handleCopyDifficulties}>
                                            📋 Copy Text
                                        </button>
                                        {copyDifficultiesStatus && <span style={{ color: BRAND.teal, fontSize: '0.85rem', fontWeight: 800 }}>{copyDifficultiesStatus}</span>}
                                    </div>
                                </div>
                                <div style={{ background: BRAND.white, border: `2px solid ${BRAND.coral}`, borderRadius: '12px', padding: '16px' }}>
                                    <p style={{ color: BRAND.coral, fontFamily: 'Nunito', fontWeight: 800, margin: '0 0 10px', fontSize: '0.9rem', lineHeight: 1.5 }}>Confirm and describe how 25% extra time (and/or a scribe, word processor with grammar/spellcheck, or speech recognition technology) is the candidate&apos;s normal way of working in the centre (i.e. in the classroom, internal tests and/or mock examinations).</p>
                                    <p style={{ color: BRAND.charcoal, fontSize: '1rem', lineHeight: 1.6, margin: '0 0 14px', fontWeight: 500 }}>{normalWayOfWorkingText}</p>
                                    <div className="jcq-no-print" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                        <button className="btn-base btn-charcoal" onClick={handleCopyNWoW}>
                                            📋 Copy Text
                                        </button>
                                        {copyNWoWStatus && <span style={{ color: BRAND.coral, fontSize: '0.85rem', fontWeight: 800 }}>{copyNWoWStatus}</span>}
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section className="jcq-print-card" style={{ borderTop: `3px solid ${BRAND.grey}`, paddingTop: '18px', marginTop: '-8px' }}>
                            <h2 style={{ margin: '0 0 8px', fontSize: '1.6rem' }}>Supporting Evidence Appendix (For Inspector Audit)</h2>
                            <p style={{ margin: 0, color: BRAND.muted, fontWeight: 600 }}>
                                Print this appendix to hold on file as your formal evidence backing up the JCQ portal submission.
                            </p>
                        </section>

                        {/* SECTION 1 */}
                        <section className="dash-card jcq-print-card" style={{ padding: '30px' }}>
                            <h2 style={{ fontSize: '1.6rem', margin: '0 0 20px', borderBottom: `2px solid ${BRAND.grey}`, paddingBottom: '10px' }}>Section 1: Picture of Need</h2>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <StatCard label="Student Name" value={baseline.name || 'Unknown'} />
                                <StatCard label="Primary Language" value={(!baseline.eal || baseline.eal === 'No') ? 'English' : (baseline.primaryLanguage || 'Not recorded')} />
                                <StatCard label="Year Group" value={formatYearGroup(baseline.yearGroup)} />
                                <StatCard label="Reading Age" value={formatValue(baseline.readingAge)} />
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <StatCard label="SEN condition(s)" value={formatValue(baseline.senConditions?.length ? baseline.senConditions : baseline.senCondition)} />
                                </div>
                                <div style={{ gridColumn: '1 / -1' }}>
                                    {(() => {
                                        const scaffoldGroups = buildActiveScaffoldingGroups(baseline, interactionSummary);
                                        const toneStyles = {
                                            teal: { bg: 'rgba(117, 185, 190, 0.12)', border: BRAND.teal, icon: '🧠' },
                                            coral: { bg: 'rgba(242, 132, 130, 0.10)', border: BRAND.coral, icon: '🎧' },
                                            warning: { bg: 'rgba(242, 204, 143, 0.20)', border: BRAND.warning, icon: '🌍' },
                                            muted: { bg: '#F6F8FA', border: '#C6D0D2', icon: '⚙️' }
                                        };
                                        return (
                                            <div className="dash-card" style={{ padding: '22px 24px', background: 'linear-gradient(135deg, #FFFFFF 0%, #F9FCFC 100%)' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '14px', alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: '16px' }}>
                                                    <div>
                                                        <p style={{ color: BRAND.muted, fontSize: '0.85rem', fontWeight: 800, margin: '0 0 6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                            Active Interventions & Scaffolding
                                                        </p>
                                                        <p style={{ color: BRAND.charcoal, fontSize: '0.95rem', fontWeight: 600, lineHeight: 1.5, margin: 0 }}>
                                                            Evidence mirrors the tutorial-generation prompts, profile settings, and any completed-attempt accessibility choices.
                                                        </p>
                                                    </div>
                                                    <span style={{ background: BRAND.charcoal, borderRadius: '999px', color: BRAND.white, fontSize: '0.78rem', fontWeight: 800, padding: '6px 12px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                                        {scaffoldGroups.reduce((count, group) => count + group.items.length, 0)} active
                                                    </span>
                                                </div>
                                                {scaffoldGroups.length === 0 ? (
                                                    <p style={{ color: BRAND.muted, margin: 0, fontStyle: 'italic', fontWeight: 500 }}>No active interventions recorded.</p>
                                                ) : (
                                                    <div style={{ display: 'grid', gap: '14px' }}>
                                                        {scaffoldGroups.map((group) => {
                                                            const tone = toneStyles[group.tone] || toneStyles.teal;
                                                            return (
                                                                <div key={group.title} style={{ background: tone.bg, border: `2px solid ${tone.border}`, borderRadius: '14px', padding: '14px 16px' }}>
                                                                    <h3 style={{ alignItems: 'center', color: BRAND.charcoal, display: 'flex', gap: '8px', fontSize: '1rem', margin: '0 0 10px' }}>
                                                                        <span aria-hidden="true">{tone.icon}</span> {group.title}
                                                                    </h3>
                                                                    <div style={{ display: 'grid', gap: '10px' }}>
                                                                        {group.items.map((item) => (
                                                                            <div key={`${group.title}-${item.label}`} style={{ background: BRAND.white, border: '1px solid rgba(45,64,66,0.14)', borderRadius: '10px', padding: '10px 12px' }}>
                                                                                <div style={{ color: BRAND.charcoal, fontFamily: 'Nunito, sans-serif', fontSize: '0.96rem', fontWeight: 800 }}>{item.label}</div>
                                                                                {item.detail && <div style={{ color: BRAND.muted, fontSize: '0.88rem', fontWeight: 600, lineHeight: 1.45, marginTop: '4px' }}>{item.detail}</div>}
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>
                        </section>

                        {/* SECTION 2 */}
                        <section className="dash-card jcq-print-card" style={{ padding: '30px' }}>
                            <h2 style={{ fontSize: '1.6rem', margin: '0 0 20px', borderBottom: `2px solid ${BRAND.grey}`, paddingBottom: '10px' }}>Section 2: Formal Assessment of Need</h2>
                            <div style={{ display: 'flex', gap: '20px', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', background: '#FFF1F0', border: `2px dashed ${BRAND.danger}`, borderRadius: '12px', padding: '20px' }}>
                                <div>
                                    <h3 style={{ margin: '0 0 6px', color: '#8A2C1D', fontSize: '1.2rem' }}>⚠️ Missing Formal Evidence</h3>
                                    <p style={{ color: '#8A2C1D', fontSize: '0.95rem', fontWeight: '600', margin: 0 }}>A formal 3-Subject JCQ Test is required for full 2024/2025 Extra Time compliance.</p>
                                </div>
                                <Link className="btn-base btn-coral jcq-no-print" to="/jcq/extra-time-test">
                                    Run Extra Time Test Now
                                </Link>
                            </div>
                        </section>

                        {/* SECTION 3 */}
                        <section className="dash-card jcq-print-card" style={{ padding: '30px' }}>
                            <h2 style={{ fontSize: '1.6rem', margin: '0 0 20px', borderBottom: `2px solid ${BRAND.grey}`, paddingBottom: '10px' }}>Section 3: History of Need and Provision</h2>
                            {subjectMetrics.length === 0 ? (
                                <p style={{ color: BRAND.muted, fontStyle: 'italic', fontWeight: 600, margin: 0 }}>No subject-level tutorial evidence is available yet.</p>
                            ) : (
                                <div className="jcq-subject-matrix" style={{ display: 'grid', gap: '30px', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                                    {subjectMetrics.map((metric) => (
                                        <SubjectMetricCard key={metric.subjectName || `${metric.averageTimePerQuestionSeconds}-${metric.timeDeficitPercent}`} metric={metric} />
                                    ))}
                                </div>
                            )}
                        </section>

                        {/* SECTION 4: AUDIT TRAIL */}
                        <section className="dash-card jcq-print-card" style={{ padding: '30px' }}>
                            <h2 style={{ fontSize: '1.6rem', margin: '0 0 8px' }}>Section 4: Intervention &amp; Scaffolding Timeline</h2>
                            <p style={{ color: BRAND.muted, fontSize: '0.95rem', margin: '0 0 24px', fontWeight: '500' }}>Chronological record of official accommodation decisions, baseline assessments, and scaffolding provision.</p>
                            
                            <div style={{ borderLeft: `3px solid ${BRAND.teal}`, marginLeft: '12px', paddingLeft: '24px' }}>
                                {timeline.length === 0 && <p style={{ color: BRAND.muted, fontWeight: '600' }}>No scaffolding changes recorded for this student.</p>}
                                
                                {timeline.map((item, index) => {
                                    const changes = getTimelineChanges(item);
                                    return (
                                        <article key={item._id || `${item.changedAt}-${index}`} style={{ background: BRAND.light, border: `2px solid #E0E0E0`, borderRadius: '12px', marginBottom: '20px', padding: '20px', position: 'relative' }}>
                                            <span style={{ background: BRAND.coral, border: `3px solid ${BRAND.cream}`, borderRadius: '50%', height: '18px', width: '18px', left: '-36px', position: 'absolute', top: '24px' }} />

                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '10px', marginBottom: '12px' }}>
                                                <h3 style={{ margin: 0, fontSize: '1.2rem', color: BRAND.charcoal }}>
                                                    {item.action === 'CREATED' ? 'Initial Provision Started' : 'Provision Adjusted'}
                                                </h3>
                                                <span style={{ color: BRAND.muted, fontSize: '0.85rem', fontWeight: 700, background: BRAND.white, border: '1px solid #D8DEE2', padding: '4px 10px', borderRadius: '20px' }}>
                                                    {item.changedAt ? new Date(item.changedAt).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : 'Unknown Date'}
                                                </span>
                                            </div>

                                            <p style={{ color: BRAND.muted, fontSize: '0.9rem', fontWeight: 600, margin: '0 0 16px' }}>Logged by: {item.changedBy || 'System/Staff'}</p>

                                            {item.subject && <p style={{ color: BRAND.charcoal, fontSize: '0.95rem', fontWeight: 700, margin: '0 0 8px' }}>Subject: {item.subject}</p>}
                                            {item.reason && <p style={{ color: BRAND.muted, fontSize: '0.95rem', fontStyle: 'italic', margin: '0 0 16px' }}>Reason: {item.reason}</p>}

                                            {changes.length === 0 ? (
                                                <p style={{ color: BRAND.muted, margin: 0, fontSize: '0.9rem', fontStyle: 'italic' }}>No specific provision changes recorded in this entry.</p>
                                            ) : (
                                                <ul style={{ margin: 0, paddingLeft: '20px', color: BRAND.charcoal, fontSize: '0.95rem', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                    {changes.map((change) => (
                                                        <li key={change.field}>
                                                            {formatTimelineObservation(change)}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </article>
                                    );
                                })}
                            </div>
                        </section>

                    </div>
                )}
            </main>

            {showForm8 && profile && (
                <Form8Modal
                    baseline={baseline}
                    difficultiesText={currentDifficultiesText}
                    nwowText={normalWayOfWorkingText}
                    onClose={() => setShowForm8(false)}
                />
            )}
            {showForm9 && profile && (
                <Form9Modal
                    baseline={baseline}
                    difficultiesText={currentDifficultiesText}
                    nwowText={normalWayOfWorkingText}
                    onClose={() => setShowForm9(false)}
                />
            )}
        </div>
    );
};

export default JCQNormalWayOfWorking;